// Protocol twin of heyjob-app/src/devices/deviceRegistration.ts; keep behavioral tests in both clients.
const revoke = (a) => ({ device_id: a.device_id, association_id: a.association_id, revocation_secret: a.revocation_secret });
export function createDeviceRegistration(deps) {
    let epoch = 0;
    let active;
    let queue = Promise.resolve();
    let remembered = null;
    const serial = (fn) => {
        const result = queue.then(() => deps.lock(fn));
        queue = result.catch(() => { });
        return result;
    };
    const load = async () => {
        const stored = await deps.load();
        remembered = stored ?? { installation_id: deps.uuid(), sequence: 0, current: null, pending: [] };
        return remembered;
    };
    const save = async (state) => { await deps.save(state); remembered = state; };
    const abortable = (work, signal) => {
        if (!signal)
            return work;
        return new Promise((resolve, reject) => {
            const stop = () => reject(new Error('Device operation cancelled'));
            signal.addEventListener('abort', stop, { once: true });
            if (signal.aborted)
                stop();
            work.then(resolve, reject).finally(() => signal.removeEventListener('abort', stop));
        });
    };
    const flush = async (state, signal) => {
        // All state mutations run under the same lock, including response handling.
        const delivered = await Promise.all([...state.pending].reverse().slice(0, 5).map(async (pending) => {
            try {
                await abortable(deps.disassociate(pending), signal);
                return pending;
            }
            catch (error) {
                return error.status === 400 ? pending : null;
            }
        }));
        state.pending = state.pending.filter(p => !delivered.some(d => d?.association_id === p.association_id && d.device_id === p.device_id));
        await save(state);
    };
    const cancel = () => { epoch += 1; active?.abort(); };
    const stale = (error) => error?.status === 409 && error.code === 'DEVICE_ASSOCIATION_STALE';
    const activate = async (state, generation) => {
        const { current: a } = state;
        active = new AbortController();
        const {signal} = active;
        const identity = { installation_id: a.installation_id, association_id: a.association_id, association_sequence: a.association_sequence, revocation_secret: a.revocation_secret };
        try {
            if (generation !== epoch)
                return;
            if (!a.device_id) {
                const prepared = await abortable(deps.register({ ...identity, operation: 'prepare' }, signal), signal);
                if (generation !== epoch)
                    return; // prepare cannot enable any token
                a.device_id = prepared.device_id;
                await save(state); // must survive process death before activation
            }
            let timer;
            const metadata = await abortable(Promise.race([
                deps.metadata(),
                new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Device metadata timeout')), 3000); }),
            ]), signal).finally(() => clearTimeout(timer));
            if (generation !== epoch)
                return;
            await abortable(deps.register({ ...identity, ...metadata, operation: 'activate' }, signal), signal);
            if (generation === epoch)
                await flush(state, signal);
        }
        catch (error) {
            if (generation === epoch)
                throw error;
        }
        finally {
            active = undefined;
        }
    };
    return {
        cancel,
        async sync(owner) {
            const generation = epoch;
            return serial(async () => {
                if (generation !== epoch)
                    return;
                const state = await load();
                for (let retried = false; ; retried = true) {
                    if (generation !== epoch)
                        return;
                    if (!state.current || state.current.owner !== owner) {
                        if (state.current?.device_id)
                            state.pending.push(revoke(state.current));
                        if (state.sequence >= 2147483647) {
                            state.installation_id = deps.uuid();
                            state.sequence = 0;
                        }
                        state.sequence += 1;
                        state.current = { installation_id: state.installation_id, association_id: deps.uuid(), association_sequence: state.sequence, revocation_secret: deps.secret(), owner };
                        await save(state); // eslint-disable-line no-await-in-loop -- one retry, strictly sequential
                    }
                    try {
                        await activate(state, generation); // eslint-disable-line no-await-in-loop -- one retry, strictly sequential
                        return;
                    }
                    catch (error) {
                        // The server revoked this association: start a new one, once. Pending revocations stay.
                        if (retried || !stale(error) || generation !== epoch)
                            throw error;
                        if (state.current.device_id && !state.pending.some(p => p.association_id === state.current.association_id && p.device_id === state.current.device_id))
                            state.pending.push(revoke(state.current));
                        state.current = null;
                    }
                }
            });
        },
        async disconnect(clearSession = async () => { }, owner = null) {
            // Hint only: the target is resolved from fresh state inside the lock.
            const hint = remembered?.current ? { ...remembered.current } : null;
            cancel();
            let clearPromise;
            const clear = () => { clearPromise ??= Promise.resolve().then(clearSession); return clearPromise; };
            let persisted = false;
            let target = null;
            const work = serial(async () => {
                try {
                    const state = await load();
                    const {current} = state;
                    // A delayed logout may never clear a newer login, even after its deadline.
                    const owned = !!current && (hint ? current.association_id === hint.association_id : !owner || current.owner === owner);
                    target = owned ? { ...current } : hint;
                    if (target?.device_id && !state.pending.some(p => p.association_id === target.association_id && p.device_id === target.device_id))
                        state.pending.push(revoke(target));
                    if (owned)
                        state.current = null;
                    await save(state);
                    persisted = true;
                    await flush(state);
                }
                catch {
                    const known = target ?? hint;
                    const pending = known?.device_id ? [revoke(known)] : [];
                    const results = await Promise.all(pending.map(p => deps.disassociate(p).then(() => true, () => false)));
                    if (results.includes(false))
                        deps.onPersistenceFailure?.();
                }
                await clear();
            }).catch(async () => { if ((target ?? hint)?.device_id && !persisted)
                deps.onPersistenceFailure?.(); await clear(); });
            let timer;
            await Promise.race([work, new Promise((resolve, reject) => {
                    timer = setTimeout(() => {
                        if (!persisted && (target ?? hint)?.device_id)
                            deps.onPersistenceFailure?.();
                        clear().then(resolve, reject);
                    }, 3000);
                })]).finally(() => clearTimeout(timer));
        },
        retryPending() {
            return serial(async () => {
                active = new AbortController();
                try {
                    const state = await load();
                    await flush(state, active.signal);
                }
                finally {
                    active = undefined;
                }
            });
        },
    };
}
